import { Dispatch, SetStateAction, FC, ChangeEvent } from "react";
import Tooltip from "@mui/material/Tooltip";
import {
    Page,
    PaginationBar,
} from "../../../assets/components/PaginationBar/PaginationBar";
import TableHeader, {
    TableHeaderRight,
} from "../../../assets/components/Table/TableHeader";
import { Transaction } from "../model";
import { useNotification } from "../../../assets/hooks/useNotification";
import Checkbox from "../../../assets/components/Checkbox/Checkbox";
import "./TransactionsTableHeader.scss";

interface Props {
    dataTable: Transaction[];
    pageNumber: Page;
    currentSizePage: number;
    sizePage: number;
    totalTransactions: number;
    setCurrentPage: Dispatch<SetStateAction<Page>>;
    selectedCount: number;
    failedCount: number;
    onRetrySelected: () => void;
    onRetryAllFailed: () => void;
    isLoading: boolean;
    isAllSelected: boolean;
    onSelectAll: (checked: boolean) => void;
}

const RETRY_PATH = "M6.21193 2.44781C7.64529 1.85192 9.23228 1.7481 10.7287 2.15237C12.225 2.55664 13.5494 3.44698 14.4967 4.68671C14.5416 4.7456 14.5935 4.7985 14.6494 4.84342L11.9704 4.86139C11.4624 4.86538 11.0531 5.27961 11.0571 5.78768C11.0601 6.29575 11.4754 6.70498 11.9834 6.70099L16.574 6.67005C16.8216 6.66805 17.0581 6.56724 17.2298 6.38856C17.4015 6.20988 17.4943 5.97034 17.4873 5.72278L17.3446 0.892766C17.3296 0.385694 16.9054 -0.0145672 16.3983 0.000406816C15.8903 0.0153793 15.491 0.43961 15.506 0.947653L15.5689 3.09369C14.415 1.78111 12.8999 0.833841 11.208 0.376648C9.31849 -0.133414 7.31527 -0.00265941 5.50663 0.748954C3.69797 1.49957 2.1868 2.83011 1.20556 4.53104C0.223361 6.23292 -0.174881 8.21117 0.0706567 10.1626C0.316205 12.114 1.19258 13.9307 2.56605 15.332C3.93952 16.7333 5.73334 17.6408 7.67076 17.9134C9.60918 18.1869 11.5816 17.8086 13.2845 16.8393C14.9864 15.8701 16.324 14.3639 17.0906 12.5541C17.2892 12.086 17.0706 11.546 16.6035 11.3483C16.1354 11.1497 15.5954 11.3683 15.3967 11.8364C14.7868 13.2758 13.7238 14.4715 12.3743 15.2401C11.0238 16.0087 9.46179 16.3081 7.92759 16.0915C6.39342 15.8759 4.97112 15.1572 3.88001 14.0443C2.79002 12.9313 2.0923 11.487 1.89667 9.93283C1.70103 8.3787 2.01844 6.80362 2.79899 5.4511C3.57956 4.09759 4.78034 3.04253 6.21261 2.44761L6.21193 2.44781Z";

const TOOLTIP_SX = {
    bgcolor: "#707070",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "18px",
    padding: "8px 13px",
    boxShadow: "0px 4px 8px rgba(255, 244, 244, 0.1)",
};

// Retry Selected — plain retry arrow (node 2225-6914)
const RetrySelectedSVG = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d={RETRY_PATH} fill="white" />
    </svg>
);

// Retry All Failed — retry arrow + "!" badge (node 2225-6945)
const RetryAllFailedSVG = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d={RETRY_PATH} fill="white" />
        <rect x="7.71" y="4.47" width="1.9" height="6.4" rx="0.95" fill="white" />
        <circle cx="8.61" cy="12.22" r="0.95" fill="white" />
    </svg>
);

const TransactionsTableHeader: FC<Props> = ({
    sizePage,
    currentSizePage,
    dataTable,
    pageNumber,
    totalTransactions,
    setCurrentPage,
    selectedCount,
    failedCount,
    onRetrySelected,
    onRetryAllFailed,
    isLoading,
    isAllSelected,
    onSelectAll,
}) => {
    const { ConfirmationMessageBox } = useNotification();

    const handleRetrySelected = async () => {
        const result = await ConfirmationMessageBox({
            textContent: {
                title: "Confirm Retry",
                text: `Are you sure you want to retry ${selectedCount} selected transaction${selectedCount > 1 ? "s" : ""}?`,
            },
        });

        if (result === "yes") {
            onRetrySelected();
        }
    };

    const handleRetryAllFailed = async () => {
        const result = await ConfirmationMessageBox({
            textContent: {
                title: "Confirm Retry",
                text: `Are you sure you want to retry all ${failedCount} failed transaction${failedCount > 1 ? "s" : ""}?`,
            },
        });

        if (result === "yes") {
            onRetryAllFailed();
        }
    };

    const handleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
        onSelectAll(e.target.checked);
        return e;
    };

    const retryActions = (
        <>
            <Tooltip
                title="Retry selected transactions on this page"
                placement="bottom-start"
                slotProps={{ tooltip: { sx: TOOLTIP_SX } }}
            >
                <span>
                    <button
                        type="button"
                        className="add_button transactions_header__retry_button"
                        disabled={selectedCount === 0 || isLoading}
                        onClick={handleRetrySelected}
                    >
                        <RetrySelectedSVG />
                    </button>
                </span>
            </Tooltip>

            <Tooltip
                title={
                    <>
                        Retry all <span style={{ color: "#F44336" }}>failed</span> transactions from entire history
                    </>
                }
                placement="bottom-start"
                slotProps={{ tooltip: { sx: TOOLTIP_SX } }}
            >
                <span>
                    <button
                        type="button"
                        className="add_button transactions_header__retry_button"
                        disabled={failedCount === 0 || isLoading}
                        onClick={handleRetryAllFailed}
                    >
                        <RetryAllFailedSVG />
                    </button>
                </span>
            </Tooltip>
        </>
    );

    return (
        <TableHeader
            pageSize={currentSizePage}
            dataTableLength={dataTable.length}
            title="List of transactions"
            headerActions={retryActions}
        >
            <>
                <PaginationBar
                    currentPage={pageNumber as number}
                    currentPageHandler={setCurrentPage}
                    totalItems={totalTransactions}
                    pageSize={sizePage}
                />

                <TableHeaderRight>
                    <div className="transactions_header__right">
                        <Checkbox
                            id="select_all_transactions"
                            name="select_all_transactions"
                            checked={isAllSelected}
                            value={isAllSelected.toString()}
                            onChange={handleSelectAll}
                        />
                    </div>
                </TableHeaderRight>
            </>
        </TableHeader>
    );
};

export default TransactionsTableHeader;
